import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useApi } from "@/lib/api";
import { fmtDate, gbp } from "@/lib/format";
import { StatusBadge, TypeTag } from "@/components/Bits";
import ResendForm from "@/components/ResendForm";

const KIND = {
  query_sent: "Query emailed", chaser_sent: "Chaser emailed", escalated: "Escalated to owners", supplier_reply: "Supplier replied",
  reply_logged: "Reply logged", resolved: "Marked resolved", auto_resolved: "Auto-resolved", resent: "Edited & resent",
};
const DOT = { supplier_reply: "bg-teal-500", escalated: "bg-rose-500", resolved: "bg-emerald-500", auto_resolved: "bg-emerald-500" };

export default function QueryDrawer({ thread, onClose, onChanged }) {
  const api = useApi();
  const [q, setQ] = useState(null);
  const [text, setText] = useState("");
  const [resolved, setResolved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const rows = await api("get", "/queries");
    setQ(rows.find((r) => r.thread === thread) || null);
  };
  useEffect(() => { if (thread) { setQ(null); setText(""); setResolved(false); load(); } }, [thread]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    try {
      await api("post", `/queries/${thread}/reply`, { text, resolved });
      toast.success(resolved ? "Marked resolved" : "Reply logged");
      setText(""); setResolved(false); await load(); onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const phone = window.matchMedia("(max-width: 639px)").matches;
  return (
    <Sheet open={!!thread} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side={phone ? "bottom" : "right"} className={`w-full overflow-y-auto sm:max-w-lg ${phone ? "max-h-[92vh] rounded-t-[28px] pb-10" : ""}`} data-testid="query-drawer">
        {phone && <div className="mx-auto -mt-2 mb-3 h-1.5 w-12 rounded-full bg-slate-200" />}
        <SheetHeader><SheetTitle className="text-left font-mono">{thread}</SheetTitle></SheetHeader>
        {!q ? <Loader2 className="mt-8 h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="mt-4 space-y-6">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2"><StatusBadge status={q.status} /><TypeTag type={q.type} /></div>
              <p className="font-display text-xl font-bold">{q.supplier} · {q.month}</p>
              <p className="text-sm text-slate-700" data-testid="query-detail">{q.detail}</p>
              {q.at_stake > 0 && (
                <div className="flex items-center justify-between rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3" data-testid="query-at-stake">
                  <span className="text-xs font-semibold uppercase tracking-wider text-rose-700">Owed back</span>
                  <span className="font-display text-2xl font-extrabold text-rose-700">{gbp(q.at_stake)}</span>
                </div>
              )}
              <p className="text-xs text-slate-500">{q.followups} chaser(s) since last contact · last contact {fmtDate(q.last_contact)}</p>
            </div>
            <ol className="relative space-y-4 border-l border-slate-200 pl-5" data-testid="query-history">
              {(q.history || []).map((h, i) => (
                <li key={i} className="relative">
                  <span className={`absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full ${DOT[h.kind] || "bg-slate-400"}`} />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{KIND[h.kind] || h.kind} · {fmtDate(h.at)}{h.by ? ` · ${h.by}` : ""}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{h.text}</p>
                </li>
              ))}
            </ol>
            {q.status !== "resolved" && <ResendForm key={q.thread} q={q} onDone={async () => { await load(); onChanged(); }} />}
            {q.status !== "resolved" && (
              <div className="space-y-3 rounded-2xl bg-slate-50 p-4">
                <p className="text-sm font-semibold">Log what the wholesaler said</p>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Credit note C502 will be on next statement" data-testid="query-reply-text" />
                <label className="flex items-center gap-2 text-sm"><Switch checked={resolved} onCheckedChange={setResolved} data-testid="query-resolve-switch" /> This sorts it out — mark resolved</label>
                <Button onClick={submit} disabled={busy || !text.trim()} className="w-full" data-testid="query-reply-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{resolved ? "Log reply & resolve" : "Log reply (keep chasing)"}
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
